import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { baseAxios } from "../../apis";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import { IconCalendar, IconLoading } from "../../assets/icons";
import moment from "moment";
import Dropdown from "../commons/Dropdown";
import InputWithIcon from "../commons/InputWithIcon";
import { extractErrorMessage, normalizeBotName } from "../../utils";
import { validateGridConfig } from "../../utils/validateBotSettings";
import { GrStatusDisabled } from "react-icons/gr";

// Available timeframes
// const timeframes = ["1m", "5m", "1h"];

const BotConfigForm = ({
  onRunBacktest,
  isRunning,
  setProgress,
  setIsRunning,
  progress,
}) => {
  // Initialize form state based on the provided JSON example
  const [bots, setBots] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [pairs, setPairs] = useState([]);
  const { fetchCsrfToken } = useCsrfToken();
  const [loading, setLoading] = useState(false);
  const [loadingPair, setLoadingPair] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [errors, setErrors] = useState({});

  const [availableDateRange, setAvailableDateRange] = useState({
    min: null,
    max: null,
  });
  const [loadingDates, setLoadingDates] = useState(false);

  const [formData, setFormData] = useState({
    // Common settings
    "common-bot": 0,
    "common-exchange": 1,
    "common-coin": 0,
    "common-start_date": null,
    "common-end_date": null,
    "common-timeframe": "1m",

    // DCA settings
    "dca-capital": 1000.0,
    "dca-amount": 10.0,
    "dca-max_order_count": 100,
    "dca-period": 0,
    "dca-take_profit_percentage": 5.0,
    "dca-trigger_price": 0.0,
    "dca-price_change_percentage": 2.0,

    // XENO settings
    "xeno-capital": 10000,
    "xeno-amount": 100,
    "xeno-grid_width": 1,
    "xeno-buy_flg_return": 0.1,
    "xeno-grid_width_exit": 1,
    "xeno-exit_flg_return": 0.1,
    "xeno-number_of_grids": 100,

    // grid
    "grid-amount": 100,
    "grid-grid_width": 1,
    "grid-grid_width_exit": 1,
    "grid-number_of_grids": 100,
    "grid-buy_after_bounce": 0,
    "grid-sell_after_bounce": 0,
    "grid-capital": 10000,

    // Rebalance settings
    // "v2-capital": "100.00",
    // "v2-follow_market_cap": true,
    // "v2-rebalance_method": "periodically", // "proportionally" or "periodically"
    // "v2-rebalance_frequency": "1d",
    // // Optional rebalance settings
    // "v2-take_profit_enabled": false,
    // "v2-take_profit_amount": "10.00",
  });

  const timeframes = ["1m"];

  // Define Date Limits: 1 Year back -> Today
  // const minDateLimit = moment().subtract(1, "year").toDate();
  // const maxDateLimit = new Date();
  const minDateLimit =
    availableDateRange?.min ?? moment().subtract(1, "year").toDate();
  const maxDateLimit = availableDateRange?.max ?? new Date();

  // State for v2 coins (array of objects)
  const [rebalanceCoins, setRebalanceCoins] = useState([
    { coin: "280", allocation: "60.00" },
    { coin: "281", allocation: "30.00" },
    { coin: "282", allocation: "10.00" },
  ]);

  // Handler for general form changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    const updated = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };

    setFormData(updated);

    const strategy = bots
      .find((bot) => bot.id === Number(updated["common-bot"]))
      ?.bot_name.toLowerCase();

    if (strategy === "grid" || strategy === "xeno arc") {
      const validationErrors = validateGridConfig(strategy, updated);
      setErrors(validationErrors);
    } else {
      setErrors({});
    }
  };

  // Handler for date changes
  const handleDateChange = (date, field) => {
    setFormData({ ...formData, [field]: moment(date).format("YYYY-MM-DD") });
  };

  // Handler for rebalance coin changes
  const handleCoinChange = (index, field, value) => {
    const updatedCoins = [...rebalanceCoins];
    updatedCoins[index] = { ...updatedCoins[index], [field]: value };
    setRebalanceCoins(updatedCoins);
  };

  // Add new coin to rebalance coins list
  const addCoin = () => {
    setRebalanceCoins([...rebalanceCoins, { coin: "", allocation: "0.00" }]);
  };

  // Remove coin from rebalance coins list
  const removeCoin = (index) => {
    setRebalanceCoins(rebalanceCoins.filter((_, i) => i !== index));
  };

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();

    // Create the final configuration object to submit
    const configData = {
      ...formData,
    };

    // Add rebalance coins data
    rebalanceCoins.forEach((coin, index) => {
      configData[`v2_coins-${index}-coin`] = coin.coin;
      configData[`v2_coins-${index}-allocation`] = coin.allocation;
    });

    configData["v2_coins-TOTAL_FORMS"] = rebalanceCoins.length.toString();
    configData["v2_coins-INITIAL_FORMS"] = "0";

    console.log("Bot Configuration:", configData);
    alert("Configuration submitted! Check console for details.");
  };

  // Run backtest handler

  const simulateProgress = () => {
    let current = 0;
    setIsRunning(true);
    const interval = setInterval(() => {
      current += Math.floor(Math.random() * 10) + 5; // random between 5 and 14

      if (current >= 90) {
        current = 90;
        setProgress(current);
        clearInterval(interval); // stop interval
      } else {
        setProgress(current);
      }
    }, 500);

    return () => clearInterval(interval); // allow cleanup if needed
  };

  const handleRunBacktest = async () => {
    setErrorText("");
    setProgress(0);

    const stopProgress = simulateProgress(); // ⏳ start fake progress

    try {
      setLoading(true);
      // Add rebalance coins data
      // rebalanceCoins.forEach((coin, index) => {
      //   configData[`v2_coins-${index}-coin`] = coin.coin;
      //   configData[`v2_coins-${index}-allocation`] = coin.allocation;
      // });

      // configData["v2_coins-TOTAL_FORMS"] = rebalanceCoins.length.toString();
      // configData["v2_coins-INITIAL_FORMS"] = "0";

      let configData = {
        "common-bot": formData["common-bot"],
        "common-exchange": formData["common-exchange"],
        "common-coin": formData["common-coin"],
        "common-start_date": formData["common-start_date"]
          ? moment(formData["common-start_date"]).format("YYYY-MM-DD")
          : null,
        "common-end_date": formData["common-end_date"]
          ? moment(formData["common-end_date"]).format("YYYY-MM-DD")
          : null,
        "common-timeframe": formData["common-timeframe"],
      };

      const strategy = bots
        .find((bot) => bot.id === Number(formData["common-bot"]))
        ?.bot_name.toLowerCase();

      if (strategy === "grid") {
        configData["grid-amount"] = formData["grid-amount"];
        configData["grid-grid_width"] = formData["grid-grid_width"];
        configData["grid-grid_width_exit"] = formData["grid-grid_width_exit"];
        configData["grid-number_of_grids"] = formData["grid-number_of_grids"];
        configData["grid-buy_after_bounce"] = formData["grid-buy_after_bounce"];
        configData["grid-sell_after_bounce"] =
          formData["grid-sell_after_bounce"];
        configData["grid-capital"] = formData["grid-capital"];
      }

      if (strategy === "dca") {
        configData["dca-capital"] = formData["dca-capital"];
        configData["dca-amount"] = formData["dca-amount"];
        configData["dca-max_order_count"] = formData["dca-max_order_count"];
        configData["dca-period"] = formData["dca-period"];
        configData["dca-take_profit_percentage"] =
          formData["dca-take_profit_percentage"];
        configData["dca-trigger_price"] = formData["dca-trigger_price"];
        configData["dca-price_change_percentage"] =
          formData["dca-price_change_percentage"];
      }

      if (strategy === "xeno arc") {
        configData["xeno-capital"] = formData["xeno-capital"];
        configData["xeno-amount"] = formData["xeno-amount"];
        configData["xeno-grid_width"] = formData["xeno-grid_width"];
        configData["xeno-buy_flg_return"] = formData["xeno-buy_flg_return"];
        configData["xeno-grid_width_exit"] = formData["xeno-grid_width_exit"];
        configData["xeno-exit_flg_return"] = formData["xeno-exit_flg_return"];
        configData["xeno-number_of_grids"] = formData["xeno-number_of_grids"];
      }

      configData["user_id"] = 1;

      const token = await fetchCsrfToken();

      // 🔐 Post with token
      const { data } = await baseAxios.post("/api/backtest/", configData, {
        headers: {
          "X-CSRFToken": token,
        },
      });

      setProgress(100);
      stopProgress(); // Stop fake progress interval

      // In a real application, we would send the configuration to the server
      // and get back actual backtest results
      onRunBacktest(
        {
          ...data,
          results: data.results.map((data) => ({
            ...data,
            pair: pairs.find(
              (coin) => coin.id === Number(formData["common-coin"]),
            ).pair,
          })),
        },
        configData,
      );
    } catch (err) {
      const errorMessage = extractErrorMessage(err?.response?.data || err);
      setErrorText(errorMessage);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsRunning(false);
      }, 800); // wait 0.8s before hiding loading
    }
  };

  useEffect(() => {
    (async function () {
      try {
        const { data } = await baseAxios.get("/api/strategy-lists");
        setBots(data.bot_master_list);
      } catch (err) {
      } finally {
      }
    })();
  }, []);

  useEffect(() => {
    if (formData["common-coin"]) {
      const fetchDateRange = async () => {
        setLoadingDates(true);
        setAvailableDateRange({ min: null, max: null });
        setFormData((prev) => ({
          ...prev,
          "common-start_date": null,
          "common-end_date": null,
        }));

        try {
          const selectedPair = pairs.find(
            (p) => p.id === formData["common-coin"],
          );
          if (!selectedPair) return;

          const coinSymbol = selectedPair.pair.split("/")[0].toLowerCase();
          const { data } = await baseAxios.get(`/api/backtest/${coinSymbol}`);

          const { start_date, end_date } = data;

          const backendStart = moment(start_date);
          const backendEnd = moment(end_date);

          const oneYearBeforeEnd = moment(backendEnd)
            .clone()
            .subtract(1, "years");

          let finalStartDate;
          let finalEndDate = backendEnd.format("YYYY-MM-DD");

          // if (backendStart.isBefore(oneYearBeforeEnd)) {
          //   finalStartDate = oneYearBeforeEnd.format("YYYY-MM-DD");
          // } else {
          //   finalStartDate = backendStart.format("YYYY-MM-DD");
          // }

          // Bypass rewrite start date
          finalStartDate = backendStart.format("YYYY-MM-DD");

          setAvailableDateRange({
            min: oneYearBeforeEnd.toDate(),
            max: backendEnd.toDate(),
          });

          setFormData((prev) => ({
            ...prev,
            "common-start_date": finalStartDate,
            "common-end_date": finalEndDate,
          }));
          // setFormData((prev) => ({
          //   ...prev,
          //   "common-start_date": moment(start_date).format("YYYY-MM-DD"),
          //   "common-end_date": moment(end_date).format("YYYY-MM-DD"),
          // }));
          // setFormData((prev) => ({
          //   ...prev,
          //   "common-start_date": new Date(start_date) ?? minDateLimit,
          //   "common-end_date": new Date(end_date) ?? maxDateLimit,
          // }));
        } catch (err) {
          console.error("Failed to fetch date range:", err);
          setErrorText("Could not load available dates for the selected coin.");
        } finally {
          setLoadingDates(false);
        }
      };

      fetchDateRange();
    }
  }, [formData["common-coin"]]);

  useEffect(() => {
    if (formData["common-exchange"]) {
      (async function () {
        setLoadingPair(true);
        setFormData((prev) => ({
          ...prev,
          "common-coin": 0,
          "common-start_date": null,
          "common-end_date": null,
        }));
        setAvailableDateRange({ min: null, max: null });
        const { data } = await baseAxios.get(
          `/api/coin_by_exchange?exchange_id=${formData["common-exchange"]}`,
        );
        setPairs(
          data.data
            .filter((dta) => dta.base_currency === "USDT")
            .map((dta) => ({
              id: dta?.id,
              pair: dta?.coin + "/USDT",
              price: dta?.coin_price || "0",
            })),
        );
        setLoadingPair(false);
      })();
    }
  }, [formData["common-exchange"]]);

  useEffect(() => {
    (async function () {
      const { data } = await baseAxios.get(`/api/get_exchanges`);

      // Extract both arrays from the response
      const allExchanges = data?.all_exchanges || [];
      const connectedExchanges = data?.connected_exchanges || [];

      // Create a Set of connected exchange IDs for quick lookup
      const connectedIds = new Set(connectedExchanges.map((item) => item?.id));

      // Map allExchanges and add `connected: true` if it exists in connectedIds
      const mergedExchanges = allExchanges.map((exchange) => ({
        id: exchange?.id,
        name:
          exchange?.exchange.charAt(0).toUpperCase() +
          exchange?.exchange.slice(1),
        status: connectedIds.has(exchange?.id) ? "connected" : "connect",
      }));

      setExchanges(mergedExchanges);

      // Auto-select first connected exchange (or first one)
      const defaultExchange =
        mergedExchanges.find((ex) => ex.status === "connected") ||
        mergedExchanges[0];
      // const defaultExchange =
      //   mergedExchanges.find((ex) => ex.status === "connected") ||
      //   mergedExchanges[0];

      if (defaultExchange) {
        setFormData((prev) => ({
          ...prev,
          ["common-exchange"]: 1,
        }));
      }
    })();
  }, []);

  const allowedCoins = [
    "BTC",
    "ETH",
    "XRP",
    "ADA",
    // "MATIC",
    "DOGE",
    "LTC",
    "DOT",
    "TRX",
    "AVAX",
    "SHIB",
    "UNI",
    "LINK",
    "XLM",
    "NEAR",
    "ATOM",
    "BNB",
    "SUI",
    "SOL",
  ];

  return (
    <form onSubmit={handleSubmit}>
      {/* Common Settings Section */}
      <div className="bg-white rounded-[10px] border border-[#DDE1E6] p-4 mb-[20px]">
        <h3 className="font-bold text-left mb-5 text-2xl">Common Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 g-3">
          <div className="flex flex-col items-start">
            <Dropdown
              id="common-bot"
              placeholder={
                formData["common-bot"]
                  ? normalizeBotName(bots.find((bot) => bot?.id === formData["common-bot"])
                      ?.bot_name)
                  : "Select Bot"
              }
              label="Bot"
              classNameLabel={"!text-sm"}
              options={bots
                .filter(
                  (bot) =>
                    bot.bot_name.toLowerCase() === "xeno arc" ||
                    bot.bot_name.toLowerCase() === "grid" ||
                    bot.bot_name.toLowerCase() === "dca",
                )
                .map((bot) => ({
                  ...bot,
                  name: normalizeBotName(bot.bot_name),
                }))}
              value={formData["common-bot"]}
              onChange={(val) =>
                setFormData({
                  ...formData,
                  ["common-bot"]: val?.id,
                })
              }
              isDialog
            />
          </div>

          {/* <div className="flex flex-col items-start">
            <Dropdown
              id="common-exchange"
              placeholder={
                formData["common-exchange"]
                  ? exchanges.find(
                      (bot) => bot?.id === formData["common-exchange"]
                    )?.name
                  : "Select Exchange"
              }
              label="Exchange"
              classNameLabel={"!text-sm"}
              options={exchanges.filter(
                (exchange) => exchange.name.toLowerCase() === "binance"
              )}
              onChange={(val) =>
                setFormData({
                  ...formData,
                  ["common-exchange"]: val?.id,
                })
              }
              isDialog
            />
          </div> */}

          <div className="flex flex-col items-start">
            <Dropdown
              id="common-coin"
              placeholder={
                loadingPair
                  ? "Loading get data"
                  : formData["common-coin"]
                    ? pairs.find((bot) => bot?.id === formData["common-coin"])
                        ?.pair
                    : "Select Pair"
              }
              label="Pair"
              classNameLabel={"!text-sm"}
              options={pairs
                .filter((coin) => {
                  const base = coin.pair.split("/")[0].toUpperCase();
                  const quote = coin.pair.split("/")[1]?.toUpperCase();

                  return quote === "USDT" && allowedCoins.includes(base);
                })
                .map((coin) => ({ ...coin, name: coin?.pair }))}
              onChange={(val) =>
                setFormData({
                  ...formData,
                  ["common-coin"]: val?.id,
                })
              }
              isDialog
            />
          </div>

          {/* <div className="flex flex-col items-start">
            <Dropdown
              id="common-timeframe"
              placeholder={
                formData["common-timeframe"]
                  ? timeframes.find(
                      (timeframe) => timeframe === formData["common-timeframe"]
                    )
                  : "Select Timeframe"
              }
              label="Timeframe"
              classNameLabel={"!text-sm"}
              options={timeframes.map((timeframe) => ({
                id: timeframe,
                name: timeframe,
              }))}
              onChange={(val) =>
                setFormData({
                  ...formData,
                  ["common-timeframe"]: val?.id,
                })
              }
              isDialog
            />
          </div> */}

          <div className="flex flex-col items-start">
            <label htmlFor="common-start_date" className="form-label">
              Start Date
            </label>
            <div className="w-full relative flex items-center bg-[#F2F4F8] shadow-[0px_1.5px_1px_#C1C7CD] rounded-xl">
              <div className="w-full relative z-1">
                <DatePicker
                  selected={
                    formData["common-start_date"]
                      ? new Date(formData["common-start_date"])
                      : null
                  }
                  onChange={(date) =>
                    handleDateChange(date, "common-start_date")
                  }
                  className="form-control m-0 !w-full !shadow-none"
                  dateFormat="yyyy-MM-dd"
                  id="common-start_date"
                  minDate={availableDateRange.min}
                  maxDate={availableDateRange.max}
                  // minDate={minDateLimit}
                  // maxDate={maxDateLimit}
                  placeholderText={
                    loadingDates ? "Loading dates..." : "Select start date"
                  }
                  disabled={!formData["common-coin"] || loadingDates}
                />
                <style>{`
          .react-datepicker-wrapper {
            width: 100% !important;
          }
        `}</style>
              </div>

              <span className="pr-5 pointer-events-none absolute top-[50%] z-0 translate-y-[-50%] right-0">
                <IconCalendar className="w-[18px] h-[18px] text-[#B7BABD]" />
              </span>
            </div>
          </div>

          <div className="flex flex-col items-start">
            <label htmlFor="common-end_date" className="form-label">
              End Date
            </label>

            <div className="w-full relative flex items-center bg-[#F2F4F8] shadow-[0px_1.5px_1px_#C1C7CD] rounded-xl">
              <div className="w-full relative z-1">
                <DatePicker
                  selected={
                    formData["common-end_date"]
                      ? new Date(formData["common-end_date"])
                      : null
                  }
                  onChange={(date) => handleDateChange(date, "common-end_date")}
                  className="form-control m-0 !w-full"
                  dateFormat="yyyy-MM-dd"
                  id="common-end_date"
                  minDate={
                    formData["common-start_date"]
                      ? new Date(formData["common-start_date"])
                      : availableDateRange.min
                  }
                  maxDate={availableDateRange.max}
                  // minDate={
                  //   formData["common-start_date"]
                  //     ? new Date(formData["common-start_date"])
                  //     : minDateLimit
                  // }
                  // maxDate={maxDateLimit}
                  placeholderText={
                    loadingDates ? "Loading dates..." : "Select end date"
                  }
                  disabled={!formData["common-coin"] || loadingDates}
                />
                <style>{`
          .react-datepicker-wrapper {
            width: 100% !important;
          }
        `}</style>
              </div>

              <span className="pr-5 pointer-events-none absolute top-[50%] z-0 translate-y-[-50%] right-0">
                <IconCalendar className="w-[18px] h-[18px] text-[#B7BABD]" />
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* DCA Settings Section */}
      {bots
        .find((bot) => bot.id === Number(formData["common-bot"]))
        ?.bot_name.toLowerCase() === "dca" && (
        <div className="bg-white rounded-[10px] border border-[#DDE1E6] p-4 mb-[20px]">
          <h3 className="font-bold text-left mb-5 text-2xl">Bot Settings</h3>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="flex flex-col items-start">
              <label htmlFor="dca-capital" className="form-label">
                Capital
              </label>

              <InputWithIcon
                type="number"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-capital"
                name="dca-capital"
                value={formData["dca-capital"]}
                onChange={handleInputChange}
                rightIcon={"USDT"}
              />
            </div>

            <div className="flex flex-col items-start">
              <label htmlFor="dca-amount" className="form-label">
                Amount
              </label>
              <InputWithIcon
                type="number"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-amount"
                name="dca-amount"
                value={formData["dca-amount"]}
                onChange={handleInputChange}
                rightIcon={"USDT"}
              />
            </div>

            <div className="flex flex-col items-start">
              <label htmlFor="dca-max_order" className="form-label">
                Maximum order
              </label>
              <InputWithIcon
                type="number"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-max_order_count"
                name="dca-max_order_count"
                value={formData["dca-max_order_count"]}
                onChange={handleInputChange}
                rightIcon={"orders"}
              />
            </div>

            {/* <div className="flex flex-col items-start">
              <label htmlFor="dca-period" className="form-label">
                Period
              </label>
              <select
                className="form-select"
                id="dca-period"
                name="dca-period"
                value={formData["dca-period"]}
                onChange={handleInputChange}
              >
                {timeframes.map((timeframe) => (
                  <option key={timeframe} value={timeframe}>
                    {timeframe}
                  </option>
                ))}
              </select>
            </div> */}

            <div className="flex flex-col items-start">
              <label
                htmlFor="dca-price_change_percentage"
                className="form-label"
              >
                Price Change
              </label>
              <InputWithIcon
                type="number"
                step="0.1"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-price_change_percentage"
                name="dca-price_change_percentage"
                value={formData["dca-price_change_percentage"]}
                onChange={handleInputChange}
                rightIcon={"%"}
              />
            </div>

            <div className="flex flex-col items-start">
              <label htmlFor="dca-take_profit" className="form-label">
                Take Profit (%)
              </label>
              <InputWithIcon
                type="number"
                step="0.1"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-take_profit_percentage"
                name="dca-take_profit_percentage"
                value={formData["dca-take_profit_percentage"]}
                onChange={handleInputChange}
                rightIcon={"%"}
              />
            </div>

            <div className="flex flex-col items-start">
              <label htmlFor="dca-trigger_price" className="form-label">
                Trigger Price
              </label>
              <InputWithIcon
                type="number"
                step="0.01"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="dca-trigger_price"
                name="dca-trigger_price"
                value={formData["dca-trigger_price"]}
                onChange={handleInputChange}
                rightIcon={"USDT"}
                placeholder="0.0 for market price"
              />
            </div>
          </div>
        </div>
      )}

      {/* XENO Settings Section */}

      {bots
        .find((bot) => bot.id === Number(formData["common-bot"]))
        ?.bot_name.toLowerCase() === "xeno arc" && (
        <div className="bg-white rounded-[10px] border border-[#DDE1E6] p-4 mb-[20px]">
          <h3 className="font-bold text-left mb-5 text-2xl">Bot Settings</h3>
          <div className="grid grid-cols-1 gap-5">
            <div className="flex flex-col items-start">
              <label htmlFor="xeno-capital" className="form-label">
                Capital
              </label>
              <InputWithIcon
                type="number"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="xeno-capital"
                name="xeno-capital"
                value={formData["xeno-capital"]}
                onChange={handleInputChange}
                rightIcon={"USDT"}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col items-start">
                <label htmlFor="xeno-amount" className="form-label">
                  Amount
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-amount"
                  name="xeno-amount"
                  value={formData["xeno-amount"]}
                  onChange={handleInputChange}
                  rightIcon={"USDT"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="xeno-number_of_grids" className="form-label">
                  Number of Grids
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-number_of_grids"
                  name="xeno-number_of_grids"
                  value={formData["xeno-number_of_grids"]}
                  onChange={handleInputChange}
                  rightIcon={"levels"}
                />

                {errors["xeno-number_of_grids"] && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors["xeno-number_of_grids"]}
                  </span>
                )}
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="xeno-grid_width" className="form-label">
                  Grid Width (%)
                </label>
                <InputWithIcon
                  type="number"
                  step="0.1"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-grid_width"
                  name="xeno-grid_width"
                  value={formData["xeno-grid_width"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="xeno-grid_width_exit" className="form-label">
                  Grid Width Exit (%)
                </label>
                <InputWithIcon
                  type="number"
                  step="0.1"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-grid_width_exit"
                  name="xeno-grid_width_exit"
                  value={formData["xeno-grid_width_exit"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="xeno-buy_flg_return" className="form-label">
                  Buy Flag Return
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-buy_flg_return"
                  name="xeno-buy_flg_return"
                  value={formData["xeno-buy_flg_return"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="xeno-exit_flg_return" className="form-label">
                  Exit Flag Return
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="xeno-exit_flg_return"
                  name="xeno-exit_flg_return"
                  value={formData["xeno-exit_flg_return"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v2 Settings Section */}
      {bots
        .find((bot) => bot.id === Number(formData["common-bot"]))
        ?.bot_name.toLowerCase() === "grid" && (
        <div className="bg-white rounded-[10px] border border-[#DDE1E6] p-4 mb-[20px]">
          <h3 className="font-bold text-left mb-5 text-2xl">Bot Settings</h3>
          <div className="grid grid-cols-1 gap-5">
            <div className="flex flex-col items-start">
              <label htmlFor="grid-capital" className="form-label">
                Capital
              </label>
              <InputWithIcon
                type="number"
                className="!w-full"
                inputClassName="!m-0 rounded-r-none"
                id="grid-capital"
                name="grid-capital"
                value={formData["grid-capital"]}
                onChange={handleInputChange}
                rightIcon={"USDT"}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="flex flex-col items-start">
                <label htmlFor="grid-amount" className="form-label">
                  Amount
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="grid-amount"
                  name="grid-amount"
                  value={formData["grid-amount"]}
                  onChange={handleInputChange}
                  rightIcon={"USDT"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="grid-number_of_grids" className="form-label">
                  Number of Grids
                </label>
                <InputWithIcon
                  type="number"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="grid-number_of_grids"
                  name="grid-number_of_grids"
                  value={formData["grid-number_of_grids"]}
                  onChange={handleInputChange}
                  rightIcon={"levels"}
                />

                {errors["grid-number_of_grids"] && (
                  <span className="text-sm text-red-500 mt-1">
                    {errors["grid-number_of_grids"]}
                  </span>
                )}
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="grid-grid_width" className="form-label">
                  Grid Width (%)
                </label>
                <InputWithIcon
                  type="number"
                  step="0.1"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="grid-grid_width"
                  name="grid-grid_width"
                  value={formData["grid-grid_width"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>

              <div className="flex flex-col items-start">
                <label htmlFor="grid-grid_width_exit" className="form-label">
                  Grid Width Exit (%)
                </label>
                <InputWithIcon
                  type="number"
                  step="0.1"
                  className="!w-full"
                  inputClassName="!m-0 rounded-r-none"
                  id="grid-grid_width_exit"
                  name="grid-grid_width_exit"
                  value={formData["grid-grid_width_exit"]}
                  onChange={handleInputChange}
                  rightIcon={"%"}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v2 Coins Section */}
      {/* <div className="bg-white rounded-[8px] p-[20px] mb-[20px] shadow-lg">
        <h3>v2 Coins</h3>
        <p className="text-muted mb-3">
          Assign allocation percentages to different coins for portfolio
          rebalancing
        </p>

        {rebalanceCoins.map((coin, index) => (
          <div className="grid grid-cols-3  gap-5 coin-row" key={index}>
            <div className="grid grid-cols-2 gap-3 col-span-2 w-full">
              <div className="flex flex-col items-start">
                <label className="form-label">Coin</label>
                <select
                  className="form-select"
                  value={coin.coin}
                  onChange={(e) =>
                    handleCoinChange(index, "coin", e.target.value)
                  }
                >
                  <option value="">Select Coin</option>
                  {availableCoins.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col items-start">
                <label className="form-label">Allocation (%)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control m-0 !w-full"
                  value={coin.allocation}
                  onChange={(e) =>
                    handleCoinChange(index, "allocation", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="flex justify-center items-center">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => removeCoin(index)}
                disabled={rebalanceCoins.length <= 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="mt-3">
          <button
            type="button"
            className="btn btn-outline-primary add-coin-button"
            onClick={addCoin}
          >
            + Add Coin
          </button>
        </div>

        <div className="mt-3">
          <p>
            Total Allocation:
            <strong>
              {` ${rebalanceCoins
                .reduce(
                  (sum, coin) => sum + parseFloat(coin.allocation || 0),
                  0
                )
                .toFixed(2)}%`}
            </strong>
            {rebalanceCoins.reduce(
              (sum, coin) => sum + parseFloat(coin.allocation || 0),
              0
            ) !== 100 && (
              <span className="text-danger ms-2">(Should equal 100%)</span>
            )}
          </p>
        </div>
      </div> */}

      {/* Submit Button */}

      {isRunning ? (
        <div className="bg-[#007A78]/10 border border-blue-300 text-blue-900 p-4 rounded mt-4">
          <div className="relative w-full bg-[#007A78]/20 rounded-full h-6 overflow-hidden">
            {/* Progress bar fill */}
            <div
              className="bg-[#007A78] h-full transition-all duration-300 ease-in-out"
              style={{ width: `${progress}%` }}
            ></div>

            {/* Centered text */}
            <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white">
              {progress === 100
                ? "Please wait a moment..."
                : `Running Backtest: ${progress}%`}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start flex-col justify-start gap-2  mt-4">
          {/* <button type="button" className="btn btn-secondary bg-red-500 me-md-2">
          Cancel
        </button> */}
          {/* <button type="submit" className="btn btn-primary me-md-2">
          Save Configuration
        </button> */}
          <button
            type="button"
            className="w-fit bg-[#007A78] hover:bg-[#007A78] text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
            onClick={handleRunBacktest}
            disabled={
              isRunning ||
              !!Object.keys(errors).length ||
              !formData["common-bot"] ||
              !formData["common-coin"] ||
              !formData["common-exchange"]
            }
          >
            {loading ? (
              <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                <IconLoading className="w-[16px] h-[16px]" />
              </div>
            ) : (
              "Run Back Test"
            )}
            {/* {isRunning ? "Running..." : "Run Backtest"} */}
          </button>
          {errorText && (
            <span className="block text-sm mb-2 text-[#ff0000]">
              {errorText}
            </span>
          )}
        </div>
      )}
    </form>
  );
};

export default BotConfigForm;
